import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { UsuarioSistema } from './models';
import workflowService from './workflowService';
import { ordenarPorNivel } from './ordenacaoUsuarios';

// N1-N7 é exclusivamente a escada de aprovação financeira (N7 = CEO, maior alçada).
// Administrador TI é uma permissão independente — configura o módulo (grupos, cargos,
// alçadas, SLA, usuários), mas não é, por si só, um nível de aprovação de conciliações.
const CARGO_POR_NIVEL: Record<number, string> = {
  1: 'Analista Contábil',
  2: 'Supervisor Contábil',
  3: 'Coordenador Contábil',
  4: 'Gerente Contábil',
  5: 'Diretor de Controladoria',
  6: 'CFO',
  7: 'CEO',
};

const NivelBadge: React.FC<{ nivel?: number; ativo?: boolean }> = ({ nivel, ativo }) => {
  if (!nivel) return <span style={{ color: '#a1a5b7', fontSize: 12 }}>Sem nível</span>;
  return (
    <span className='wf-nivel-badge' style={{ opacity: ativo === false ? 0.5 : 1 }}>
      N{nivel} — {CARGO_POR_NIVEL[nivel] ?? ''}
    </span>
  );
};

const AdminTiBadge: React.FC<{ ativo?: boolean }> = ({ ativo }) => (
  <span style={{
    background: '#e8f4fb', color: '#005e96', fontWeight: 700,
    fontSize: 11, padding: '3px 10px', borderRadius: 20, display: 'inline-flex', alignItems: 'center', gap: 5,
    border: '1px solid #b8d8f0', opacity: ativo === false ? 0.5 : 1,
  }}>
    🔧 Administrador TI
  </span>
);

const CadastroUsuariosPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ fromOnboarding?: boolean; etapa?: number }>();
  const fromOnboarding = location.state?.fromOnboarding ?? false;
  const onboardingEtapa = location.state?.etapa ?? 2;
  const { usuario: usuarioLogado } = useAuth();
  const [usuarios, setUsuarios] = useState<UsuarioSistema[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');

  // Modal de atribuição de nível completo (N1-N7)
  const [modal, setModal] = useState<{ usuario: UsuarioSistema } | null>(null);
  const [form, setForm] = useState({ nivel: 1, cargo: CARGO_POR_NIVEL[1] });
  const [salvando, setSalvando] = useState(false);

  // Confirmação rápida de marcar/desmarcar como TI
  const [confirmTi, setConfirmTi] = useState<{ usuario: UsuarioSistema; acao: 'marcar' | 'remover' } | null>(null);
  const [salvandoTi, setSalvandoTi] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4500);
  };

  useEffect(() => {
    const clienteId = usuarioLogado?.clienteID;
    if (!clienteId) { setLoading(false); return; }

    const p1 = workflowService.listarUsuariosSistema(clienteId).catch(() => [] as UsuarioSistema[]);
    const p2 = workflowService.listarUsuarios().catch(() => [] as any[]);

    Promise.all([p1, p2]).then(([sistema, wf]) => {
      // Chave = authUserId (link para o sistema principal), não o PK interno do workflow
      const wfMap = new Map(wf.map((u: any) => [u.authUserId, u]));
      const merged = sistema.map(u => {
        const wfUsuario = wfMap.get(u.id) as any;
        return {
          ...u,
          wfUserId: wfUsuario?.id,
          wfNivel:  wfUsuario?.nivel,
          wfCargo:  wfUsuario?.cargo,
          wfAtivo:  wfUsuario?.ativo,
          wfAdminTi: wfUsuario?.adminTi ?? false,
          bloqueado: u.bloqueado === true || wfUsuario?.bloqueado === true,
        };
      });
      setUsuarios(merged);
    }).finally(() => setLoading(false));
  }, [usuarioLogado]);

  // ─── Atribuição rápida de TI (N7) ─────────────────────────────────────────

  const confirmarMarcarTi = (u: UsuarioSistema) =>
    setConfirmTi({ usuario: u, acao: 'marcar' });

  const confirmarRemoverTi = (u: UsuarioSistema) =>
    setConfirmTi({ usuario: u, acao: 'remover' });

  const executarAcaoTi = async () => {
    if (!confirmTi) return;
    const { usuario, acao } = confirmTi;
    const ativo = acao === 'marcar';
    setSalvandoTi(true);
    try {
      const resultado = await workflowService.setAdminTi({
        authUserId: usuario.id,
        nome:       usuario.nome,
        email:      usuario.email,
        ativo,
      });
      setUsuarios(prev => prev.map(u =>
        u.id === usuario.id
          ? { ...u, wfUserId: resultado.id, wfNivel: u.wfNivel ?? resultado.nivel, wfCargo: u.wfCargo ?? resultado.cargo, wfAtivo: true, wfAdminTi: ativo }
          : u
      ));
      showToast(ativo
        ? `${usuario.nome} agora é Administrador TI. Pode configurar o módulo Workflow SOX. Log SOX registrado.`
        : `Acesso de Administrador TI de ${usuario.nome} removido. Log SOX registrado.`);
      setConfirmTi(null);
    } catch {
      showToast('Erro ao alterar acesso de Administrador TI. Tente novamente.');
    } finally {
      setSalvandoTi(false);
    }
  };

  // ─── Modal de nível completo (N1-N6 aprovadores) ──────────────────────────

  const abrirModal = (u: UsuarioSistema) => {
    const nivel = u.wfNivel ?? 1;
    setForm({ nivel, cargo: u.wfCargo ?? CARGO_POR_NIVEL[nivel] });
    setModal({ usuario: u });
  };

  const onNivelChange = (nivel: number) => {
    setForm({ nivel, cargo: CARGO_POR_NIVEL[nivel] });
  };

  const salvar = async () => {
    if (!modal) return;
    setSalvando(true);
    try {
      const resultado = await workflowService.atribuirNivel({
        authUserId:       modal.usuario.id,
        nome:             modal.usuario.nome,
        email:            modal.usuario.email,
        nivelHierarquico: form.nivel,
        nomeCargo:        form.cargo,
      });
      setUsuarios(prev => prev.map(u =>
        u.id === modal.usuario.id
          ? { ...u, wfUserId: resultado.id, wfNivel: resultado.nivel, wfCargo: resultado.cargo, wfAtivo: true }
          : u
      ));
      showToast(`Nível N${form.nivel} — ${form.cargo} atribuído a ${modal.usuario.nome}. Log SOX registrado.`);
      setModal(null);
    } catch {
      showToast('Erro ao atribuir nível. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const removerNivel = async (u: UsuarioSistema) => {
    if (!u.wfUserId) return;
    try {
      await workflowService.desativarUsuario(u.wfUserId);
      setUsuarios(prev => prev.map(x =>
        x.id === u.id ? { ...x, wfAtivo: false } : x
      ));
      showToast(`Acesso workflow de ${u.nome} desativado. Log SOX registrado.`);
    } catch {
      showToast('Erro ao remover nível.');
    }
  };

  const filtrados = usuarios.filter(u =>
    !search ||
    u.nome.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  // Administrador TI (configura o módulo) e Aprovador (nível N1-N7, assina conciliações)
  // são permissões independentes — um usuário pode ter uma, outra, as duas ou nenhuma.
  // Todas as listas saem ordenadas por nível (N1 → N7), com o mesmo nível agrupado.
  const adminsTi     = ordenarPorNivel(filtrados.filter(u => u.wfAdminTi && u.wfAtivo !== false));
  const aprovadores  = ordenarPorNivel(filtrados.filter(u => u.wfNivel && u.wfAtivo !== false));
  // "Sem acesso" é a lista de quem pode receber uma nova atribuição: bloqueados ficam de fora.
  // Bloqueados que já têm nível/admin continuam nas listas acima para poderem ser revisados.
  const semAcesso    = ordenarPorNivel(filtrados.filter(u => ((!u.wfAdminTi && !u.wfNivel) || u.wfAtivo === false) && !u.bloqueado));

  return (
    <div className='wf-page'>
      <div className='wf-breadcrumb'>Home › Governança de TI › Usuários do Workflow SOX</div>
      <div className='wf-header'>
        <div>
          <h1>Usuários do Workflow SOX</h1>
          <p>Defina quem é Administrador TI (configura o módulo) e quem é Aprovador N1–N7 (assina conciliações) — são permissões independentes.</p>
        </div>
        <div className='wf-header__actions'>
          {fromOnboarding && (
            <button
              className='wf-btn wf-btn--secondary'
              onClick={() => history.push('/workflow-aprovacao/onboarding', { goToEtapa: onboardingEtapa })}
            >
              ← Onboarding Etapa {onboardingEtapa}
            </button>
          )}
          {!fromOnboarding && <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>}
        </div>
      </div>

      {toast && <div className='wf-toast wf-toast--success'><strong>SOX</strong> {toast}</div>}

      {/* Explicação dos papéis */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#e8f4fb', border: '1px solid #b8d8f0', borderRadius: 10, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 20 }}>🔧</span>
            <strong style={{ color: '#005e96', fontSize: 14 }}>Administrador TI</strong>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#005e96' }}>
            Pode criar grupos, configurar alçadas, SLA, cargos e ativar o Go-Live. Não aprova conciliações por si só — é uma permissão de configuração, independente do nível de aprovação.
            <br /><strong>Obrigatório ter pelo menos 1 antes de qualquer configuração.</strong>
          </p>
        </div>
        <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 10, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 20 }}>✍️</span>
            <strong style={{ color: '#181c32', fontSize: 14 }}>Aprovadores (N1–N7)</strong>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#7e8299' }}>
            Recebem e assinam conciliações conforme o nível de alçada configurado (N7 = CEO, maior alçada financeira). Segregação de funções (SoD) validada automaticamente.
          </p>
        </div>
      </div>

      {/* Barra de busca */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='Pesquisar por nome ou e-mail...'
          className='wf-search-inline'
        />
      </div>

      {loading && (
        <div className='wf-count' style={{ textAlign: 'center', padding: 40 }}>Carregando usuários...</div>
      )}

      {!loading && (
        <>
          {/* Seção: Administradores TI */}
          <section className='wf-card' style={{ marginBottom: 20 }}>
            <div className='wf-card__header' style={{ background: '#e8f4fb', borderRadius: '10px 10px 0 0', padding: '14px 20px', margin: '-1px -1px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>🔧</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: 15, color: '#005e96' }}>Administradores TI</h2>
                  <p style={{ margin: 0, fontSize: 12, color: '#4a90c4' }}>{adminsTi.length} usuário(s) com acesso de configuração</p>
                </div>
              </div>
            </div>
            <div className='wf-table-wrap'>
              <table className='wf-table'>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Perfil sistema</th>
                    <th>Nível de aprovação</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {adminsTi.length === 0 && (
                    <tr>
                      <td colSpan={5}>
                        <div style={{ padding: '20px 0', textAlign: 'center' }}>
                          <span style={{ fontSize: 24 }}>⚠️</span>
                          <p style={{ margin: '8px 0 0', color: '#f6c000', fontWeight: 600, fontSize: 13 }}>
                            Nenhum Administrador TI cadastrado. Selecione um usuário abaixo e clique em "Marcar como Administrador TI".
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {adminsTi.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.nome}</strong></td>
                      <td style={{ fontSize: 12 }}>{u.email}</td>
                      <td><span className='wf-badge wf-badge--em_analise'>{u.perfil || '—'}</span></td>
                      <td><NivelBadge nivel={u.wfNivel} ativo={u.wfAtivo} /></td>
                      <td className='wf-table-actions'>
                        <button
                          className='wf-btn wf-btn--danger-outline wf-btn--sm'
                          onClick={() => confirmarRemoverTi(u)}
                        >
                          Remover TI
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Seção: Aprovadores N1-N7 */}
          {aprovadores.length > 0 && (
            <section className='wf-card' style={{ marginBottom: 20 }}>
              <div className='wf-card__header'>
                <div>
                  <h2>Aprovadores (N1–N7)</h2>
                  <p>{aprovadores.length} aprovador(es) ativo(s)</p>
                </div>
              </div>
              <div className='wf-table-wrap'>
                <table className='wf-table'>
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>E-mail</th>
                      <th>Nível</th>
                      <th>Cargo Workflow SOX</th>
                      <th>Admin TI</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aprovadores.map(u => (
                      <tr key={u.id}>
                        <td><strong>{u.nome}</strong></td>
                        <td style={{ fontSize: 12 }}>{u.email}</td>
                        <td><NivelBadge nivel={u.wfNivel} ativo={u.wfAtivo} /></td>
                        <td style={{ fontSize: 12 }}>{u.wfCargo ?? '—'}</td>
                        <td>{u.wfAdminTi ? <AdminTiBadge ativo={u.wfAtivo} /> : <span style={{ color: '#a1a5b7', fontSize: 12 }}>—</span>}</td>
                        <td className='wf-table-actions'>
                          <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => abrirModal(u)}>Editar nível</button>
                          <button className='wf-btn wf-btn--danger-outline wf-btn--sm' onClick={() => removerNivel(u)}>Remover</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Seção: Usuários sem acesso */}
          <section className='wf-card'>
            <div className='wf-card__header'>
              <div>
                <h2>Usuários sem acesso ao Workflow SOX</h2>
                <p>{semAcesso.filter(u => !u.wfNivel && !u.wfAdminTi).length} sem nenhuma permissão · {semAcesso.filter(u => u.wfAtivo === false).length} desativado(s)</p>
              </div>
            </div>
            <div className='wf-table-wrap'>
              <table className='wf-table'>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Perfil sistema</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {semAcesso.length === 0 && (
                    <tr><td colSpan={4} className='wf-count' style={{ textAlign: 'center' }}>Todos os usuários têm acesso ao Workflow SOX.</td></tr>
                  )}
                  {semAcesso.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.nome}</strong></td>
                      <td style={{ fontSize: 12 }}>{u.email}</td>
                      <td><span className='wf-badge wf-badge--em_analise'>{u.perfil || '—'}</span></td>
                      <td className='wf-table-actions'>
                        <button
                          className='wf-btn wf-btn--primary wf-btn--sm'
                          style={{ background: '#005e96', borderColor: '#005e96' }}
                          onClick={() => confirmarMarcarTi(u)}
                        >
                          🔧 Marcar como Administrador TI
                        </button>
                        <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => abrirModal(u)}>
                          Atribuir aprovador
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {/* Modal de confirmação TI */}
      {confirmTi && (
        <div className='wf-modal-bg'>
          <div className='wf-modal'>
            <div className='wf-modal__header'>
              <div>
                <h2>
                  {confirmTi.acao === 'marcar'
                    ? '🔧 Marcar como Administrador TI'
                    : 'Remover acesso de Administrador TI'}
                </h2>
                <p>{confirmTi.usuario.nome} — {confirmTi.usuario.email}</p>
              </div>
              <button onClick={() => setConfirmTi(null)}>×</button>
            </div>
            <div className='wf-modal__body'>
              {confirmTi.acao === 'marcar' ? (
                <div className='wf-sod'>
                  <b>🔧</b>
                  <div>
                    <strong>Acesso Administrador TI</strong>
                    <p>
                      Este usuário poderá criar grupos econômicos, configurar alçadas, SLA, cargos e ativar o Go-Live do Workflow SOX.
                      Essa permissão não inclui aprovar conciliações — para isso, atribua também um nível N1-N7 separadamente.
                      Um registro imutável SOX será gerado automaticamente.
                    </p>
                  </div>
                </div>
              ) : (
                <div className='wf-review'>
                  <strong>⚠️ Remover acesso de Administrador TI</strong>
                  <p>
                    O usuário perderá a capacidade de configurar o módulo Workflow SOX. Se ele também tiver nível de aprovação (N1-N7),
                    esse continua ativo normalmente. As configurações existentes serão mantidas. O histórico SOX permanece intacto.
                  </p>
                </div>
              )}
            </div>
            <div className='wf-modal__footer'>
              <button className='wf-btn wf-btn--secondary' onClick={() => setConfirmTi(null)} disabled={salvandoTi}>Cancelar</button>
              <button
                className={`wf-btn ${confirmTi.acao === 'marcar' ? 'wf-btn--primary' : 'wf-btn--danger-outline'}`}
                onClick={executarAcaoTi}
                disabled={salvandoTi}
              >
                {salvandoTi
                  ? 'Salvando...'
                  : confirmTi.acao === 'marcar' ? 'Confirmar — Marcar como Administrador TI' : 'Confirmar — Remover Administrador TI'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de nível N1-N7 (aprovadores) */}
      {modal && (
        <div className='wf-modal-bg'>
          <div className='wf-modal'>
            <div className='wf-modal__header'>
              <div>
                <h2>{modal.usuario.wfNivel ? 'Editar nível aprovador' : 'Atribuir nível aprovador'}</h2>
                <p>{modal.usuario.nome} — {modal.usuario.email}</p>
              </div>
              <button onClick={() => setModal(null)}>×</button>
            </div>
            <div className='wf-modal__body'>
              <div className='wf-review' style={{ marginBottom: 14 }}>
                <strong>ℹ️ Aprovadores (N1–N7)</strong>
                <p>Aprovadores recebem conciliações conforme seu nível de alçada (N7 = CEO, maior alçada financeira). Para acesso de configuração do módulo, use "Marcar como Administrador TI" na listagem principal — é uma permissão separada.</p>
              </div>
              <div className='wf-form-grid'>
                <label>
                  Nível hierárquico *
                  <select
                    value={form.nivel}
                    onChange={e => onNivelChange(Number(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map(n => (
                      <option key={n} value={n}>N{n} — {CARGO_POR_NIVEL[n]}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Cargo workflow *
                  <input
                    value={form.cargo}
                    onChange={e => { const v = e.target.value; setForm(f => ({ ...f, cargo: v })); }}
                    placeholder='Ex: Analista Contábil'
                  />
                </label>
              </div>
              {modal.usuario.wfNivel && modal.usuario.wfNivel !== form.nivel && (
                <div className='wf-review' style={{ marginTop: 14 }}>
                  <strong>Alteração de cargo</strong>
                  <p>Mudança de N{modal.usuario.wfNivel} para N{form.nivel} gerará registro imutável no histórico SOX.</p>
                </div>
              )}
            </div>
            <div className='wf-modal__footer'>
              <button className='wf-btn wf-btn--secondary' onClick={() => setModal(null)}>Cancelar</button>
              <button
                className='wf-btn wf-btn--primary'
                disabled={salvando || !form.cargo.trim()}
                onClick={salvar}
              >
                {salvando ? 'Salvando...' : modal.usuario.wfNivel ? 'Salvar alterações' : 'Atribuir nível'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CadastroUsuariosPage;
